import React, { useCallback, useEffect, useRef, useState } from "react";
import { useRefetchOn } from "@/hooks/useRefetchOn";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { BackButton } from "@/common/BackButton";
import { useDispatch, useSelector } from "react-redux";
import {
  CustomTable,
  MobileTable,
  CustomCheckbox,
  type CustomColumnDef,
  CustomSectionTitle,
  CustomRenderCellWithDash,
  CustomRenderPriceCellWithDash,
  CustomLoading,
  CustomModal,
  CustomButton,
} from "@ippon/shared-components";
import { getAiAssessmentColors } from "../../../theme/aiAssessment";
import {
  fetchRepairById,
  selectRepairEstimate,
} from "../../../store/repairSlice";
import type { AppDispatch, RootState } from "../../../store";
import { useGlobalToast } from "../../../hooks/useGlobalToast";
import { useRedirectWithToast } from "../../../hooks/useRedirectWithToast";
import ipponApi from "../../../config/axios";

type EstimateDetailState = {
  title?: string;
  companyName?: string;
  amount?: number;
  status?: "未対応" | "進行中" | "完了";
};

const PDF_LOAD_TIMEOUT_MS = 10000;

const overallScaleLabels = [
  "相場より圧倒的に低い",
  "相場より低い",
  "適正",
  "相場より高い",
  "相場より圧倒的に高い",
] as const;

// マークダウン記号を削除する関数
const removeMarkdown = (text: string | undefined | null): string => {
  if (!text) return "";

  return text
    .split("\n")
    .map((line) => {
      // 行頭のマークダウン記号を削除
      line = line.replace(/^>\s*/g, ""); // 引用
      line = line.replace(/^[-+*]\s+/g, ""); // リスト
      line = line.replace(/^\d+\.\s+/g, ""); // 番号付きリスト
      line = line.replace(/^[-_*]{3,}$/g, ""); // 水平線

      // インラインのマークダウン記号を削除
      line = line.replace(/~+/g, "");
      line = line.replace(/[*_]+/g, "");
      line = line.replace(/#+/g, "");
      line = line.replace(/`+/g, "");
      line = line.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1"); // リンク
      line = line.replace(/。+/g, "。"); // 連続句点

      return line.trim();
    })
    .filter((line) => line.length > 0)
    .join("\n")
    .trim();
};

const EstimateDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { state } = useLocation() as {
    state?: EstimateDetailState & {
      repairId?: string;
      propName?: string;
      repairTitle?: string;
      fromEstimateList?: boolean;
      propertyId?: string;
      estimateTitle?: string;
    };
  };
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { current } = useSelector((s: RootState) => s.repairs);
  const [activeTab, setActiveTab] = React.useState<"document" | "ai">(
    "document",
  );
  const { showToast } = useGlobalToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const repairIdFromNav = state?.repairId;

  // AI チャットアクション完了時 (refetchBus.emit('estimate:<id>')) に再フェッチ。
  // estimate は repair に内包されるため repair 単位で再取得する。
  const refetchEstimate = useCallback(() => {
    if (repairIdFromNav) {
      void dispatch(fetchRepairById({ repair_id: repairIdFromNav }));
    }
  }, [dispatch, repairIdFromNav]);
  useRefetchOn("estimate", id, refetchEstimate);
  const [propertyName, setPropertyName] = useState<string>(
    state?.propName || "",
  );
  const [pdfLoading, setPdfLoading] = useState(true);

  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const redirectToList = useRedirectWithToast(
    "/estimates",
    "見積書が見つかりません",
  );

  // Fetch repair by repairId (from state); then pick estimate by id
  useEffect(() => {
    if (!id || id === "undefined" || !repairIdFromNav) return redirectToList();
    (async () => {
      try {
        const result = await dispatch(
          fetchRepairById({ repair_id: repairIdFromNav }),
        );
        if (!fetchRepairById.fulfilled.match(result)) return redirectToList();
        const p = result.payload as {
          repair_estimates?: unknown[];
          data?: { repair_estimates?: unknown[] };
        };
        const raw = p?.repair_estimates ?? p?.data?.repair_estimates;
        const estimates = Array.isArray(raw) ? raw : [];
        const found = estimates.some(
          (e) =>
            String(
              (e as { repair_estimate_id?: unknown }).repair_estimate_id,
            ) === String(id),
        );
        if (!found) redirectToList();
      } catch (err) {
        console.error("Error fetching estimate detail:", err);
        redirectToList();
      }
    })();
  }, [dispatch, id, repairIdFromNav, redirectToList]);

  const currentEstimate = (current?.data?.repair_estimates || []).find(
    (e: any) => String(e.repair_estimate_id) === String(id),
  );
  const estimateDetailLoading = Boolean(
    current?.loading || (!currentEstimate && repairIdFromNav),
  );

  // パンくずと同じ整形ルールでタイトルを生成
  const getCleanTitle = (fileName: string): string => {
    if (!fileName) return "";
    return fileName
      .replace(/^[a-f0-9-]+_/, "") // UUID接頭辞の除去
      .replace(/^【御見積】/, "") // 接頭辞の除去
      .replace(/\.pdf$/i, "") // 拡張子の除去（大文字小文字）
      .replace(/ \(\d+\)$/, ""); // 末尾の (2) 等を除去
  };

  const displayTitle =
    getCleanTitle(currentEstimate?.file_name || "") ||
    (state?.title ?? currentEstimate?.estimate_company_name ?? "見積書");

  const estimateDisplayTitle =
    getCleanTitle(currentEstimate?.file_name || "") ||
    currentEstimate?.estimate_company_name ||
    "見積書";

  const displayAmount = currentEstimate?.estimate_amount ?? state?.amount;
  const rawStatus = (currentEstimate?.status || state?.status || "") as
    | "未対応"
    | "進行中"
    | "完了"
    | "査定済み"
    | "";
  const displayStatus = (
    rawStatus === "査定済み" || !rawStatus ? "未対応" : rawStatus
  ) as "未対応" | "進行中" | "完了";

  useEffect(() => {
    const fetchPropertyName = async (propertyId: string) => {
      try {
        const BaseURL = import.meta.env.VITE_API_URL;
        const res = await ipponApi.get(
          `${BaseURL}/api/v1/properties/${propertyId}`,
        );
        const propertyData = res?.data?.data;
        if (propertyData && propertyData.name) {
          setPropertyName(propertyData.name);
        }
      } catch (_e) {
        setPropertyName("物件名");
      }
    };

    if (!propertyName && current?.data?.property_id) {
      fetchPropertyName(current.data.property_id);
    }
  }, [current?.data?.property_id, propertyName]);

  const lastBreadcrumbState = useRef<string>("");
  useEffect(() => {
    if (!estimateDisplayTitle) return;

    const repairTitle = state?.repairTitle || current?.data?.repair_title || "";
    const repairId = state?.repairId || current?.data?.repair_id || "";
    const propertyId = current?.data?.property_id || state?.propertyId;

    const stateKey = `${estimateDisplayTitle}|${repairTitle}|${repairId}|${propertyName}|${propertyId}`;
    if (stateKey === lastBreadcrumbState.current) return;
    lastBreadcrumbState.current = stateKey;

    const nextState: Record<string, unknown> = state ? { ...state } : {};
    // データ取得中に「見積書」固定文字列が breadcrumb 等に流れないよう空フォールバック
    const resolvedEstimateTitle = estimateDisplayTitle || state?.title || "";
    navigate(`/estimates/${id}`, {
      state: {
        ...nextState,
        propName: propertyName || undefined,
        repairTitle: repairTitle || undefined,
        estimateTitle: resolvedEstimateTitle,
        repairId: repairId || undefined,
        propertyId: propertyId || undefined,
      },
      replace: true,
    });
  }, [
    propertyName,
    current?.data?.repair_title,
    current?.data?.repair_id,
    current?.data?.property_id,
    estimateDisplayTitle,
    navigate,
    id,
    state,
  ]);

  // 選定モーダル制御
  const [selectModalOpen, setSelectModalOpen] = useState(false);
  const [submittingSelect, setSubmittingSelect] = useState(false);

  // 既存紐付き支出の有無で文言/挙動が変わる
  const hasLinkedExpense = Boolean((currentEstimate as any)?.linked_expense_id);
  const linkedExpenseId: string | undefined = (currentEstimate as any)
    ?.linked_expense_id;
  const isSelected = Boolean(
    (currentEstimate as any)?.is_selected === true || displayStatus === "完了",
  );

  const handleCheckboxClick = () => {
    if (isSelected) return; // 選定済みは解除不可
    setSelectModalOpen(true);
  };

  const runSelect = async (createExpense: boolean) => {
    if (!current?.data?.repair_id || !id) return;
    setSubmittingSelect(true);
    try {
      await dispatch(
        selectRepairEstimate({
          repair_id: String(current.data.repair_id),
          repair_estimate_id: String(id),
          create_expense: createExpense,
        }),
      ).unwrap();
      showToast({
        message: hasLinkedExpense
          ? "支払い済みに更新しました"
          : createExpense
            ? "支出を登録しました"
            : "選定済みにしました",
        type: "success",
      });
      await dispatch(
        fetchRepairById({ repair_id: String(current.data.repair_id) }),
      );
      setSelectModalOpen(false);
    } catch (err: any) {
      showToast({
        message: typeof err === "string" ? err : "選定に失敗しました",
        type: "error",
      });
    } finally {
      setSubmittingSelect(false);
    }
  };

  const goToLinkedExpense = () => {
    if (!linkedExpenseId) return;
    const params = new URLSearchParams();
    const propertyId = current?.data?.property_id || state?.propertyId || "";
    if (propertyId) params.set("property_id", String(propertyId));
    const qs = params.toString();
    navigate(`/expenses/${linkedExpenseId}${qs ? `?${qs}` : ""}`, {
      state: {
        propName: propertyName || state?.propName,
        propertyId,
      },
    });
  };
  const displayOverall = currentEstimate?.ai_overall_decision || "-";

  // AI総合診断の色を決定
  const getOverallColor = (overall: string) =>
    getAiAssessmentColors(overall).color;
  const getOverallBackground = (overall: string) =>
    getAiAssessmentColors(overall).borderColor;
  // const displayFileName = currentEstimate?.file_name || "ファイル名未設定";
  // const displayUploadedAt = currentEstimate?.uploaded_at || "";
  const pdfEmbedUrl = currentEstimate?.repair_image
    ? `${currentEstimate.repair_image}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&view=FitH`
    : "";

  useEffect(() => {
    if (activeTab !== "document" || !pdfEmbedUrl) {
      setPdfLoading(false);
      return;
    }

    setPdfLoading(true);
    const timer = setTimeout(() => {
      setPdfLoading(false);
    }, PDF_LOAD_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [activeTab, pdfEmbedUrl]);

  const infoCardSx = {
    background: "#fff",
    border: "1px solid #d9d9d9",
    borderRadius: "12px",
    px: 2.5,
    py: 1.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flex: 1,
  } as const;

  const chipSx = {
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    height: 24,
    px: 1.5,
    display: "flex",
    alignItems: "center",
  } as const;

  const columns: CustomColumnDef[] = [
    { field: "id", headerName: "No.", flex: 0.8 },
    { field: "name", headerName: "項目", flex: 1.5 },
    {
      field: "price",
      headerName: "見積金額",
      flex: 1.5,
      renderCell: CustomRenderPriceCellWithDash,
    },
    {
      field: "comment",
      headerName: "AI評価・コメント",
      flex: 4,
      renderCell: CustomRenderCellWithDash,
    },
  ];

  const formatPriceCell = (v: unknown): string => {
    if (v === null || v === undefined) return "";

    let num: number;
    if (typeof v === "number") {
      num = v;
    } else {
      const s = String(v).trim();
      // すでにカンマや通貨記号が含まれている場合は一旦数値化して正規化
      const parsedNum = Number(s.replace(/[^0-9.-]/g, ""));
      if (isNaN(parsedNum)) {
        return s;
      }
      num = parsedNum;
    }

    if (num === 0) return "";
    return `¥${num.toLocaleString("ja-JP")}`;
  };

  const tableRows = React.useMemo(() => {
    const arr = Array.isArray(currentEstimate?.detailed_evaluation)
      ? (currentEstimate!.detailed_evaluation as any[])
      : [];
    return arr.map((r: any, idx: number) => ({
      id: idx + 1,
      name: r.name ?? r["項目"] ?? "",
      price: formatPriceCell(r.price ?? r["金額"] ?? ""),
      comment: removeMarkdown(r.comment ?? r["コメント"] ?? ""),
    }));
  }, [currentEstimate?.detailed_evaluation]);

  return (
    <Box
      // pt は sticky タイトルの「外」に置くとスクロール時に動くので常に 0。
      // 上余白は sticky タイトル Box の内側 pt で持たせる (他の詳細ページと同じ)。
      pt={0}
      pb={{ xs: "24px", sm: "36px" }}
      data-entity="estimate"
      data-id={id || "new"}
    >
      <Box
        sx={{
          mb: 3,
          // タイトルを固定ヘッダー(50px)の下に sticky 固定。
          // 上余白は他の詳細ページ(支出・工事)と同じ 0.5 に揃える。
          pt: 0.5,
          position: "sticky",
          top: 50,
          zIndex: 10,
          backgroundColor: "white",
        }}
      >
        <CustomSectionTitle
          title={displayTitle}
          size="large"
          addBorder
          icon={<BackButton size={24} />}
        />
      </Box>

      {/* 上部カード */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box sx={{ ...infoCardSx, flex: { xs: "auto", md: 1 } }}>
          <Typography
            sx={{
              fontSize: { xs: 14, md: 16 },
              fontWeight: 700,
              color: "#3e3e3e",
            }}
          >
            見積書記載金額
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 18, md: 20 },
              fontWeight: 700,
              color: "#3e3e3e",
            }}
          >
            {(() => {
              const raw = displayAmount as unknown as any;
              const n =
                typeof raw === "number"
                  ? raw
                  : typeof raw === "string"
                    ? Number(raw.replace(/[^0-9.-]/g, ""))
                    : NaN;
              return Number.isFinite(n) ? n.toLocaleString("ja-JP") : "-";
            })()}
            円
          </Typography>
        </Box>
        <Box sx={{ ...infoCardSx, flex: { xs: "auto", md: 1 } }}>
          <Typography
            sx={{
              fontSize: { xs: 14, md: 16 },
              fontWeight: 700,
              color: "#3e3e3e",
            }}
          >
            AI総合診断
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: 14, md: 16 },
              fontWeight: 700,
              color: getOverallColor(displayOverall),
            }}
          >
            {displayOverall}
          </Typography>
        </Box>
        <Box
          sx={{
            ...infoCardSx,
            flex: { xs: "auto", md: 1 },
            backgroundColor: "#fff",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 14, md: 16 },
              fontWeight: 700,
              color: "#3e3e3e",
            }}
          >
            選定可否
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {isSelected ? (
              linkedExpenseId ? (
                <Box
                  component="span"
                  onClick={goToLinkedExpense}
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#3E3E3E",
                    textDecoration: "underline",
                    cursor: "pointer",
                    "&:hover": { color: "#0E9DBC" },
                  }}
                >
                  支出登録済み
                </Box>
              ) : (
                <Box
                  component="span"
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#3E3E3E",
                  }}
                >
                  選定済み
                </Box>
              )
            ) : (
              <CustomCheckbox checked={false} onChange={handleCheckboxClick} />
            )}
          </Box>
        </Box>
      </Box>

      {/* カテゴリ（Figma準拠カード） */}
      <Box mt={1}>
        <Box
          sx={{
            background: "#fff",
            border: "1px solid #d9d9d9",
            borderRadius: "12px",
            px: 2.5,
            py: 1.5,
          }}
        >
          <Typography
            sx={{ fontSize: 16, fontWeight: 700, color: "#3e3e3e", mb: 1.5 }}
          >
            AIカテゴリ
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {(() => {
              const rawCategory = current?.data?.category;
              const categories = Array.isArray(rawCategory)
                ? rawCategory
                : typeof rawCategory === "string"
                  ? [rawCategory]
                  : [];
              return categories.length > 0 ? (
                categories.map((c, idx) => (
                  <Box key={idx} sx={chipSx}>
                    <Typography sx={{ fontSize: 12, color: "#3e3e3e" }}>
                      {c}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography sx={{ fontSize: 12, color: "#989898" }}>
                  -
                </Typography>
              );
            })()}
          </Box>
        </Box>
      </Box>

      {/* タブ見出し（Figma準拠） */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 0,
          mt: 1,
          pl: 0,
          pb: 0,
          position: "relative",
          flexWrap: "wrap",
        }}
      >
        {/* 下線 */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            borderBottom: "1px solid #d9d9d9",
          }}
        />

        {/* 書類確認タブ */}
        <Box
          onClick={() => setActiveTab("document")}
          sx={{
            cursor: "pointer",
            height: 30,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            position: "relative",
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: activeTab === "document" ? "#0b9dbd" : "#989898",
              lineHeight: 1.7,
            }}
          >
            書類確認
          </Typography>
          {activeTab === "document" && (
            <Box
              sx={{
                position: "absolute",
                left: -0.5,
                right: -0.5,
                bottom: -1,
                height: 31,
                borderBottom: "2px solid #0b9dbd",
                borderTopLeftRadius: "10px",
                borderTopRightRadius: "10px",
              }}
            />
          )}
        </Box>

        {/* AI査定結果タブ */}
        <Box
          onClick={() => setActiveTab("ai")}
          sx={{
            cursor: "pointer",
            position: "relative",
            height: 30,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
          }}
        >
          <Box
            sx={
              activeTab === "ai"
                ? {
                    position: "absolute",
                    left: -0.5,
                    right: -0.5,
                    bottom: -1,
                    height: 31,
                    borderBottom: "2px solid #0b9dbd",
                    borderTopLeftRadius: "10px",
                    borderTopRightRadius: "10px",
                  }
                : { display: "none" }
            }
          />
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: activeTab === "ai" ? "#0b9dbd" : "#989898",
              lineHeight: 1.7,
            }}
          >
            AI査定結果
          </Typography>
        </Box>
      </Box>
      {/* タブ内容 */}
      {activeTab === "document" ? (
        <Box mt={1}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {/* 単一：アップロードファイル（横幅拡張） */}
            <Box
              sx={{
                flex: 1,
                minWidth: { xs: 0, md: 960 },
                width: { xs: "100%", md: "auto" },
                background: "#fff",
                border: "1px solid #d9d9d9",
                borderRadius: "10px",
                p: { xs: 2, md: 3 },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 14, md: 16 },
                  fontWeight: 700,
                  color: "#3e3e3e",
                  mb: 1.5,
                }}
              >
                アップロードファイル
              </Typography>
              <Box
                sx={{
                  border: "1px solid #e9e9e9",
                  borderRadius: "8px",
                  background: "#f7f7f7",
                  height: { xs: 400, md: 640 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#989898",
                  p: 1,
                }}
              >
                {currentEstimate?.repair_image ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      backgroundColor: "#F5F5F5",
                    }}
                  >
                    {pdfLoading && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(245,245,245,0.9)",
                          zIndex: 1,
                        }}
                      >
                        <CustomLoading
                          loading={true}
                          fullScreen={false}
                          size="medium"
                        />
                      </Box>
                    )}
                    <object
                      data={pdfEmbedUrl}
                      type="application/pdf"
                      style={{ width: "100%", height: "100%" }}
                      onLoad={() => setPdfLoading(false)}
                    >
                      <Typography
                        sx={{ fontSize: 12, color: "#989898", mb: 1 }}
                      >
                        プレビューを表示できません
                      </Typography>
                      <a
                        href={currentEstimate.repair_image}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          color: "#0b9dbd",
                          textDecoration: "underline",
                          fontWeight: 700,
                        }}
                      >
                        新規タブで開く
                      </a>
                    </object>
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: 12, color: "#989898" }}>
                    ファイルプレビュー
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      ) : estimateDetailLoading ? (
        <Box
          mt={1}
          sx={{
            minHeight: 360,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fff",
            border: "1px solid #d9d9d9",
            borderRadius: "10px",
          }}
        >
          <CustomLoading loading={true} fullScreen={false} size="medium" />
        </Box>
      ) : (
        <>
          {/* 診断結果 */}
          <Box mt={1}>
            <Box
              sx={{
                background: "#fff",
                border: "1px solid #d9d9d9",
                borderRadius: "10px",
                p: { xs: 2, md: 3 },
              }}
            >
              <Box>
                <Typography
                  sx={{
                    // fontSize: { xs: 16, md: 18 },
                    fontSize: { xs: 14, md: 16 },
                    fontWeight: 700,
                    color: "#3e3e3e",
                    mb: 1.5,
                  }}
                >
                  {/* 診断結果 */}
                  AI総合診断結果
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    height: { xs: "auto", sm: 60 },
                    borderRadius: "32px",
                    overflow: "hidden",
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  {overallScaleLabels.map((label, i) => {
                    const isActive = label === displayOverall;
                    return (
                      <Box
                        key={label}
                        sx={{
                          background: isActive
                            ? getOverallBackground(label)
                            : "#f5f5f5",
                          flex: { xs: "auto", sm: 1 },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          py: { xs: 1, sm: 0 },
                          borderRight: {
                            xs: "none",
                            sm:
                              i === overallScaleLabels.length - 1
                                ? "none"
                                : "1px solid #d9d9d9",
                          },
                          borderBottom: {
                            xs:
                              i === overallScaleLabels.length - 1
                                ? "none"
                                : "1px solid #d9d9d9",
                            sm: "none",
                          },
                          ...(i === 0
                            ? {
                                borderTopLeftRadius: { xs: 8, sm: 32 },
                                borderBottomLeftRadius: { xs: 0, sm: 32 },
                                borderTopRightRadius: { xs: 8, sm: 0 },
                              }
                            : {}),
                          ...(i === overallScaleLabels.length - 1
                            ? {
                                borderTopRightRadius: { xs: 8, sm: 32 },
                                borderBottomRightRadius: { xs: 8, sm: 32 },
                                borderBottomLeftRadius: { xs: 8, sm: 0 },
                              }
                            : {}),
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: { xs: 12, md: 14 },
                            fontWeight: 700,
                            color: isActive
                              ? getOverallColor(label)
                              : "#6e778a",
                          }}
                        >
                          {label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* カテゴリ分類 */}
              <Box mt={1}>
                <Typography
                  sx={{
                    // fontSize: 14,
                    fontSize: { xs: 14, md: 16 },
                    fontWeight: 700,
                    color: "#3e3e3e",
                    mb: 1,
                  }}
                >
                  カテゴリ分類
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {(() => {
                    const rawCategory = current?.data?.category;
                    const categories = Array.isArray(rawCategory)
                      ? rawCategory
                      : typeof rawCategory === "string"
                        ? [rawCategory]
                        : [];
                    return categories.length > 0 ? (
                      categories.map((c, idx) => (
                        <Box key={idx} sx={chipSx}>
                          <Typography sx={{ fontSize: 12, color: "#3e3e3e" }}>
                            {c}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography sx={{ fontSize: 12, color: "#989898" }}>
                        -
                      </Typography>
                    );
                  })()}
                </Box>
              </Box>

              {/* 理由 */}
              <Box mt={1}>
                <Typography
                  sx={{
                    // fontSize: 14,
                    fontSize: { xs: 14, md: 16 },
                    fontWeight: 700,
                    color: "#3e3e3e",
                    mb: 1,
                  }}
                >
                  概要
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "#3e3e3e",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {removeMarkdown(currentEstimate?.reason) || "-"}
                </Typography>
              </Box>

              {/* コメント */}
              {/* <Box mt={1}>
                <Typography
                  sx={{
                    // fontSize: 14,
                    fontSize: { xs: 14, md: 16 },
                    fontWeight: 700,
                    color: "#3e3e3e",
                    mb: 1,
                  }}
                >
                  コメント
                </Typography>
                <Typography sx={{ fontSize: 14, color: "#3e3e3e" }}>
                  {currentEstimate?.comment || "-"}
                </Typography>
              </Box> */}
            </Box>
          </Box>

          {/* 明細評価 */}
          <Box mt={1}>
            <Box
              sx={{
                background: "#fff",
                border: "1px solid #d9d9d9",
                borderRadius: "10px",
                p: { xs: 2, md: 3 },
              }}
            >
              <Typography
                sx={{
                  // fontSize: { xs: 16, md: 18 },
                  fontSize: { xs: 14, md: 16 },
                  fontWeight: 700,
                  color: "#3e3e3e",
                  mb: 1.5,
                }}
              >
                明細評価
              </Typography>
              <Box sx={{ mb: 1 }}>
                {isMobile ? (
                  <MobileTable
                    rows={tableRows}
                    columns={columns}
                    hidePagination
                    checkBoxOpen={false}
                    disableNameClick={true}
                    noDataMessage="明細評価が登録されていません"
                  />
                ) : (
                  <CustomTable
                    rows={tableRows}
                    columns={columns}
                    pagination={undefined}
                    hidePagination
                    minHeight="0px"
                    rowHeight={44}
                    outlined={false}
                    noDataMessage="明細評価が登録されていません"
                  />
                )}
              </Box>

              {/* <Box sx={{ mb: 1, mt: 2 }}>
                <Typography
                  sx={{
                    // fontSize: 14,
                    fontSize: { xs: 14, md: 16 },
                    fontWeight: 700,
                    color: "#3e3e3e",
                    mb: 1,
                  }}
                >
                  異常値チェック
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 24 }}>
                  {Array.isArray(currentEstimate?.anomaly_checks) &&
                  currentEstimate!.anomaly_checks!.length > 0 ? (
                    currentEstimate!.anomaly_checks!.map(
                      (t: string, i: number) => <li key={i}>{t}</li>
                    )
                  ) : (
                    <li>異常値は検出されていません</li>
                  )}
                </ul>
              </Box> */}
            </Box>
          </Box>
        </>
      )}

      {/* 見積選定モーダル: 紐付き支出の有無で文言と挙動が変わる */}
      <CustomModal
        openModal={selectModalOpen}
        handleCloseModal={() => {
          if (!submittingSelect) setSelectModalOpen(false);
        }}
        title={hasLinkedExpense ? "支払い済みに更新" : "支出として登録"}
        bodyText=""
        modalType="deleteModal"
        hideCloseButton
      >
        <Box>
          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            {hasLinkedExpense
              ? "この見積に紐づく支出のステータスを「支払い済み」に更新しますか?"
              : "この見積の内容を支出として登録しますか?"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          {hasLinkedExpense ? (
            <>
              <CustomButton
                label="キャンセル"
                buttonCategory="cancelModal"
                disabled={submittingSelect}
                onClick={() => setSelectModalOpen(false)}
              />
              <CustomButton
                label="はい"
                buttonCategory="submit"
                disabled={submittingSelect}
                onClick={() => runSelect(false)}
                sx={{ width: "50% !important" }}
              />
            </>
          ) : (
            <>
              <CustomButton
                label="いいえ"
                buttonCategory="cancelModal"
                disabled={submittingSelect}
                onClick={() => runSelect(false)}
              />
              <CustomButton
                label="はい"
                buttonCategory="submit"
                disabled={submittingSelect}
                onClick={() => runSelect(true)}
                sx={{ width: "50% !important" }}
              />
            </>
          )}
        </Box>
      </CustomModal>
    </Box>
  );
};

export default EstimateDetailPage;
